import { isAxiosError } from 'axios';
import { z } from 'zod';
import { WidgetDefinitionsService } from '@inference.engine/api-client/web';
import type { RenderReport } from '@inference.engine/widget-html/web';
import { inferenceHttp } from '@/lib/api/inference-client';
import { SUPPRESS_ERROR_NOTIFICATION_HEADER } from '@/lib/api/interceptors/problem-details.interceptor';

const placementSchema = z.enum(['inline', 'panel']);
const sampleSchema = z.object({ name: z.string(), data: z.record(z.string(), z.unknown()) });

/** What publishing a widget changes against its published version. */
export const widgetChangesSchema = z.object({
  firstVersion: z.boolean(),
  documentLinesChanged: z.number(),
  fieldsAdded: z.array(z.string()),
  fieldsRemoved: z.array(z.string()),
  placement: z.object({ from: placementSchema, to: placementSchema }).optional(),
  samplesChanged: z.boolean(),
});

export const widgetDefinitionSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  slug: z.string(),
  scope: z.enum(['org', 'workspace', 'user']),
  workspaceIds: z.array(z.string()),
  status: z.enum(['draft', 'published', 'archived']),
  placement: placementSchema,
  ownerUserId: z.string(),
  draftRevision: z.number(),
  publishedVersion: z.number().nullable(),
  hasUnpublishedChanges: z.boolean(),
  usedBy: z.array(z.object({ id: z.string(), name: z.string() })),
  /** No agent's draft or live version shows it, so it can be deleted. */
  deletable: z.boolean(),
  /** Only on a single widget: the agents whose published version shows it. */
  publishedAgents: z.array(z.object({ id: z.string(), name: z.string() })).optional(),
  draft: z
    .object({
      document: z.string(),
      dataSchema: z.record(z.string(), z.unknown()),
      samples: z.array(sampleSchema),
      placement: placementSchema,
      runtimeHash: z.string(),
    })
    .optional(),
  changes: widgetChangesSchema.optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  archivedAt: z.string().nullable(),
});
export type WidgetDefinition = z.infer<typeof widgetDefinitionSchema>;
export type WidgetSample = z.infer<typeof sampleSchema>;

const listSchema = z.object({
  data: z.array(widgetDefinitionSchema),
  nextCursor: z.string().nullable().optional(),
  hasMore: z.boolean(),
});

export const widgetVersionSchema = z.object({
  definitionId: z.string(),
  version: z.number(),
  name: z.string(),
  description: z.string(),
  document: z.string(),
  dataSchema: z.record(z.string(), z.unknown()),
  samples: z.array(sampleSchema),
  placement: placementSchema,
  runtimeHash: z.string(),
  publishedBy: z.string(),
  publishedAt: z.string(),
  note: z.string().nullable(),
});
export type WidgetVersion = z.infer<typeof widgetVersionSchema>;

const problemSchema = z.object({ line: z.number(), code: z.string(), message: z.string() });
export const widgetCheckSchema = z.object({
  ok: z.boolean(),
  problems: z.array(problemSchema),
  warnings: z.array(problemSchema),
});
export type WidgetCheck = z.infer<typeof widgetCheckSchema>;

export interface WidgetListFilters {
  status?: WidgetDefinition['status'];
  placement?: WidgetDefinition['placement'];
  q?: string;
}

const dataOf = async (request: Promise<unknown>) => ((await request) as { data: unknown }).data;

/** For a call whose caller reports its own failure, or that the person never asked for. */
// eslint-disable-next-line i18next/no-literal-string -- request header value, never rendered
const QUIET = { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' };

/** Every widget in scope, all pages read. */
export async function listWidgets(filters: WidgetListFilters = {}): Promise<WidgetDefinition[]> {
  const all: WidgetDefinition[] = [];
  let cursor: string | undefined;
  do {
    const page = listSchema.parse(
      await dataOf(
        WidgetDefinitionsService.list({
          client: inferenceHttp,
          throwOnError: true,
          query: { pageSize: 100, ...filters, ...(cursor ? { cursor } : {}) },
        }),
      ),
    );
    all.push(...page.data);
    cursor = page.hasMore ? (page.nextCursor ?? undefined) : undefined;
  } while (cursor);
  return all;
}

export const getWidget = async (id: string) =>
  widgetDefinitionSchema.parse(
    await dataOf(
      WidgetDefinitionsService.get({ client: inferenceHttp, throwOnError: true, path: { id } }),
    ),
  );

export const createWidget = async (
  body: {
    name: string;
    placement: WidgetDefinition['placement'];
    scope: WidgetDefinition['scope'];
    workspaceIds?: string[];
    description?: string;
  },
  idempotencyKey: string,
) =>
  widgetDefinitionSchema.parse(
    await dataOf(
      WidgetDefinitionsService.create({
        client: inferenceHttp,
        throwOnError: true,
        body,
        headers: { 'Idempotency-Key': idempotencyKey },
      }),
    ),
  );

/** Refused (409, `widget_in_use`) while any agent still shows the widget. */
export const deleteWidget = async (id: string): Promise<void> => {
  await WidgetDefinitionsService.remove({
    client: inferenceHttp,
    throwOnError: true,
    path: { id },
  });
};

export const restoreWidgetVersion = async (
  id: string,
  body: { version: number; draftRevision: number },
) =>
  widgetDefinitionSchema.parse(
    await dataOf(
      // The restore dialog shows its own failure.
      WidgetDefinitionsService.restoreVersion({
        client: inferenceHttp,
        throwOnError: true,
        path: { id },
        body,
        headers: QUIET,
      }),
    ),
  );

export const updateWidget = async (
  id: string,
  body: { name?: string; description?: string; status?: 'archived' | 'restored' },
) =>
  widgetDefinitionSchema.parse(
    await dataOf(
      WidgetDefinitionsService.update({
        client: inferenceHttp,
        throwOnError: true,
        path: { id },
        body,
      }),
    ),
  );

export const checkWidget = async (id: string) =>
  widgetCheckSchema.parse(
    await dataOf(
      WidgetDefinitionsService.check({ client: inferenceHttp, throwOnError: true, path: { id } }),
    ),
  );

const versionPageSchema = z.object({
  data: z.array(widgetVersionSchema),
  nextCursor: z.string().nullable().optional(),
  hasMore: z.boolean(),
});

/** A widget's whole history, newest first, all pages read. */
export async function listWidgetVersions(id: string): Promise<WidgetVersion[]> {
  const all: WidgetVersion[] = [];
  let cursor: string | undefined;
  do {
    const page = versionPageSchema.parse(
      await dataOf(
        WidgetDefinitionsService.listVersions({
          client: inferenceHttp,
          throwOnError: true,
          path: { id },
          query: { pageSize: 100, ...(cursor ? { cursor } : {}) },
        }),
      ),
    );
    all.push(...page.data);
    cursor = page.hasMore ? (page.nextCursor ?? undefined) : undefined;
  } while (cursor);
  return all;
}

export async function uploadWidgetPreview(
  id: string,
  shot: {
    draftRevision: number;
    width: 350 | 636 | 965;
    theme: 'light' | 'dark';
    report: RenderReport;
    image?: Blob;
  },
): Promise<void> {
  // Captured in the background, and refused (409) once the draft has moved on.
  await WidgetDefinitionsService.uploadPreview({
    client: inferenceHttp,
    throwOnError: true,
    headers: QUIET,
    path: { id },
    body: {
      draftRevision: shot.draftRevision,
      width: shot.width,
      theme: shot.theme,
      report: JSON.stringify(shot.report),
      ...(shot.image ? { image: shot.image } : {}),
    },
  });
}

/** The widget's latest light preview, or null while it has none. */
export async function fetchWidgetThumbnail(id: string): Promise<Blob | null> {
  try {
    const response = await WidgetDefinitionsService.thumbnail({
      client: inferenceHttp,
      throwOnError: true,
      path: { id },
      responseType: 'blob',
    });
    return (response as { data: Blob }).data;
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 404) return null;
    throw error;
  }
}
